/* global Raven */
import { stringify } from "query-string"
import * as cookies from "@kiwicom/nitro/lib/services/session/cookies"
import { USER_ID } from "@kiwicom/nitro/lib/consts/cookies"
import * as session from "@kiwicom/nitro/lib/services/session/session"
import { SESSION_ID } from "@kiwicom/nitro/lib/consts/session"

import promiseRetry, { AbortError } from "./promiseRetry"

/* eslint-disable no-param-reassign */

// Improvements over basic fetch
// Add content-type header
// Return json in case of success
// Return error detail and text in case of failure

const hasIgnoreCaseKey = (obj, key: string) => {
  const lKey = key.toLocaleLowerCase()
  return Object.keys(obj).some(x => x.toLocaleLowerCase() === lKey)
}

const JSON_CONTENT_TYPE = "application/json"
const NO_CONTENT = 204
// const captureBreadcrumb = (typeof Raven !== 'undefined' && Raven.captureBreadcrumb) ?
//     Raven.captureBreadcrumb.bind(Raven) :
//     (data) => console.log('http request', data)

// Attempt for flow types

export type Options = {
  method?: "GET" | "PUT" | "POST" | "DELETE",
  body?: any,
  headers?: *,
  query?: any,
  retry?: Number,
}

export type Settings = {
  requestId?: boolean,
}

const handleError = (data: any, response: Response, requestId: boolean = false) => {
  const err = new Error(response.statusText)
  err.status = response.status
  err.statusText = response.statusText
  err.data = data
  if (requestId) {
    err.headerRequestId = response.headers.get("request-id")
  }

  throw err
}

export const handleResponse = (response: Response, requestId: boolean = false) => {
  const headerRequestId = response.headers.get("request-id")
  if (response.ok) {
    if (response.status === NO_CONTENT) return null
    const contentType = response.headers && response.headers.get("content-type")
    if (!contentType || contentType.includes("application/json")) {
      if (requestId && headerRequestId) {
        return response.json().then(data => ({ ...data, headerRequestId }))
      }
      return response.json()
    }
    return response.text()
  }

  // error
  if (response.json) {
    return response.json().then(data => handleError(data, response))
  }
  return response.text().then(data => handleError(data, response))
}

const removeNullValues = obj =>
  Object.keys(obj)
    .filter(k => obj[k] == null)
    .forEach(k => delete obj[k])

export const getUrlWithQuery = (url: string, query: any) => {
  const separator = url.includes("?") ? "&" : "?"
  // filter out null and undefined values,
  // queryString.stringify serialize it other way then we need
  if (typeof query === "object") removeNullValues(query)
  return `${url}${separator}${stringify(query)}`
}

export default function jsonFetch(url: string, options: Options, settings: Settings = {}) {
  options = options || {}
  options.headers = options.headers || {}
  const missingHeader = key => !hasIgnoreCaseKey(options.headers, key)
  if (missingHeader("Content-Type")) {
    options.headers["Content-Type"] = JSON_CONTENT_TYPE
  }
  if (missingHeader("Accept")) {
    options.headers.Accept = JSON_CONTENT_TYPE
  }

  if (typeof window !== "undefined" && window.SP_GLOBALS && !options.disableTracking) {
    options.headers["X-WHOIAM"] = cookies.load(USER_ID) || ""
    options.headers["X-WHOIAM-SESSION"] = session.load(SESSION_ID) || ""
  }

  if (options.body) {
    if (typeof options.body === "object") {
      options.body = JSON.stringify(options.body)
    }
    if (options.method && options.method.toUpperCase() === "GET") {
      throw new Error("Wrong method, cannot send body with GET")
    }
    if (!options.method) options.method = "POST"
  } else {
    // Edge browser don't work when you send options.body = null
    delete options.body
  }

  // Warn non-standard
  if (options.query) {
    url = getUrlWithQuery(url, options.query)
    delete options.query
  }

  if (options.retry && options.retry > 1) {
    const run = () =>
      fetch(url, options).then(response => {
        // abort retrying if the resource doesn't exist
        if (response.status === 404) {
          throw new AbortError(response.statusText)
        }

        return handleResponse(response, settings.requestId || false)
      })
    return promiseRetry(run, {
      retries: options.retry,
      factor: 1.1,
      minTimeout: 10,
      maxTimeout: 500,
    })
  }
  return fetch(url, options)
    .then(handleResponse)
    .catch(ex => {
      if (typeof Raven !== "undefined" && Raven.captureException) Raven.captureException(ex)
      throw ex
    })
}
